import {
  MILEAGE_RATE,
  STANDARD_HOURS,
  TRAVEL_DEDUCTION_MINUTES,
  KIT_AUTO_HOURS,
  MULTIPLIER_LABELS,
  ABSENCE_CATEGORIES,
} from './constants';
import {
  durationMinutes,
  minutesToHours,
  listBandSegments,
} from './otBands';

export const isWeekday = workDate => {
  if (!workDate) return true;
  const day = new Date(`${workDate}T12:00:00`).getDay();
  return day !== 0 && day !== 6;
};

export const canCombineStandardHours = category =>
  !ABSENCE_CATEGORIES.includes(category);

export const calculateMileagePounds = miles => {
  const n = Number(miles);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.round(n * MILEAGE_RATE * 100) / 100;
};

/**
 * Standard travel: payable = max(0, duration - 30 mins)
 * On-call travel: door-to-door, no deduction
 */
export const calculatePayableTravelMinutes = ({
  startTime,
  endTime,
  onCallTravel,
}) => {
  const raw = durationMinutes(startTime, endTime);
  if (raw == null) return null;
  if (onCallTravel) return raw;
  return Math.max(0, raw - TRAVEL_DEDUCTION_MINUTES);
};

export const calculateKitHours = collectingKit =>
  collectingKit ? KIT_AUTO_HOURS : 0;

/**
 * Build calculation snapshot for an entry (stored + shown in UI/export).
 */
const STANDARD_HOUR_CHOICES = [8, 8.5, 6];

const chosenStandardHours = entry => {
  const hours = Number(entry.standardHours);
  if (STANDARD_HOUR_CHOICES.indexOf(hours) !== -1) return hours;
  return STANDARD_HOURS;
};

export const calculateEntry = (entry = {}) => {
  const category = entry.category;
  const standardRow = category === 'standard_hours';
  const startTime = standardRow ? '' : entry.startTime;
  const endTime = standardRow ? '' : entry.endTime;
  const onCallTravel = category === 'on_call_travel';
  const isTravel = category === 'travel' || onCallTravel;

  const rawTravelMinutes =
    isTravel && startTime && endTime
      ? durationMinutes(startTime, endTime)
      : null;

  const payableTravelMinutes = isTravel
    ? calculatePayableTravelMinutes({
        startTime,
        endTime,
        onCallTravel,
      })
    : null;

  const segments =
    startTime && endTime
      ? listBandSegments({
          workDate: entry.workDate,
          startTime,
          endTime,
          category,
        }).map(segment => ({
          ...segment,
          multiplierLabel:
            MULTIPLIER_LABELS[segment.multiplier] || `×${segment.multiplier}`,
        }))
      : [];

  const splitsBands =
    category === 'overtime' || category === 'on_call_onsite';
  let multiplier = 1;
  if (category === 'on_call_travel') multiplier = 1.5;
  else if (splitsBands && segments.length === 1) {
    multiplier = segments[0].multiplier;
  } else if (splitsBands && segments.length > 1) {
    multiplier = segments.reduce(
      (highest, segment) =>
        segment.multiplier > highest ? segment.multiplier : highest,
      1,
    );
  }
  const multiplierLabel =
    splitsBands && segments.length > 1
      ? segments.map(segment => segment.multiplierLabel).join(', ')
      : MULTIPLIER_LABELS[multiplier] || `×${multiplier}`;

  const workedStandardHours =
    !!entry.workedStandardHours &&
    isWeekday(entry.workDate) &&
    canCombineStandardHours(category);

  const mileageMiles =
    entry.mileageMiles === '' || entry.mileageMiles == null
      ? 0
      : Number(entry.mileageMiles);

  const mileagePounds = calculateMileagePounds(mileageMiles);

  const collectingKit = category === 'collecting_kit';
  const kitHours = calculateKitHours(collectingKit);

  let timedHours = null;
  if (startTime && endTime && !isTravel && !standardRow) {
    const mins = durationMinutes(startTime, endTime);
    timedHours = mins != null ? minutesToHours(mins) : null;
  }
  if (isTravel && payableTravelMinutes != null) {
    timedHours = minutesToHours(payableTravelMinutes);
  }

  return {
    workedStandardHours,
    standardHours: workedStandardHours ? chosenStandardHours(entry) : 0,
    rawTravelMinutes,
    payableTravelMinutes,
    payableTravelHours:
      payableTravelMinutes != null
        ? minutesToHours(payableTravelMinutes)
        : null,
    travelDeductionApplied: isTravel && !onCallTravel,
    mileageMiles: Number.isNaN(mileageMiles) ? 0 : mileageMiles,
    mileagePounds,
    kitHours,
    collectingKit,
    kitCollectionTime: collectingKit ? entry.kitCollectionTime || null : null,
    timedHours,
    multiplier,
    multiplierLabel,
    segments,
  };
};

/**
 * Merge form/API payload with calc snapshot into a persistable entry shape.
 */
export const buildCalculatedEntry = base => {
  const calc = calculateEntry(base);
  return {
    ...base,
    workedStandardHours: calc.workedStandardHours,
    mileageMiles: calc.mileageMiles,
    collectingKit: calc.collectingKit,
    kitCollectionTime: calc.kitCollectionTime,
    calc,
    multiplierLabel: calc.multiplierLabel,
  };
};
